import { getToken } from '@shared/services/api';
import type {
  Plan,
  CreatePlanRequest,
  AddAddonRequest,
  AddAddonResponse,
  SubscriptionAddon,
  SubscriptionResponse,
} from '../types/plan';

const API_BASE = '/api/v1';

// Helper to transform snake_case API response to camelCase
function transformPlan(plan: any): Plan {
  const addonStripeIds = plan.features?.addon_stripe_ids || {};

  return {
    id: plan.id,
    name: plan.name,
    displayName: plan.display_name,
    description: plan.description,
    monthlyPrice: plan.monthly_price,
    currency: plan.currency,
    agentsIncluded: plan.agents_included,
    storageIncludedGB: plan.storage_included_gb,
    maxAgents: plan.max_agents,
    extraAgentPrice: plan.extra_agent_price,
    extraAgentStorageGB: plan.extra_agent_storage_gb,
    extraStoragePrice: plan.extra_storage_price,
    extraStorageAmountGB: plan.extra_storage_amount_gb,
    features: plan.features || {},
    stripePriceId: plan.stripe_price_id,
    stripeProductId: plan.stripe_product_id,
    addonStripeIds: {
      agentAddonProductId: addonStripeIds.agent_addon_product_id,
      agentAddonPriceId: addonStripeIds.agent_addon_price_id,
      storageAddonProductId: addonStripeIds.storage_addon_product_id,
      storageAddonPriceId: addonStripeIds.storage_addon_price_id,
    },
    isActive: plan.is_active,
    isPublic: plan.is_public,
    sortOrder: plan.sort_order,
    baseMargin: plan.base_margin,
    createdAt: plan.created_at,
    updatedAt: plan.updated_at,
    formattedPrice: plan.formatted_price,
    hasExtraAgents: plan.has_extra_agents,
    hasExtraStorage: plan.has_extra_storage,
    hasUnlimitedAgents: plan.has_unlimited_agents,
    stripeSynced: plan.stripe_synced,
    stripeSyncError: plan.stripe_sync_error,
  };
}

// Helper to transform camelCase request to snake_case for API
function transformPlanRequest(plan: Partial<CreatePlanRequest>): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (plan.name !== undefined) result.name = plan.name;
  if (plan.displayName !== undefined) result.display_name = plan.displayName;
  if (plan.description !== undefined) result.description = plan.description;
  if (plan.monthlyPrice !== undefined) result.monthly_price = plan.monthlyPrice;
  if (plan.currency !== undefined) result.currency = plan.currency;
  if (plan.agentsIncluded !== undefined) result.agents_included = plan.agentsIncluded;
  if (plan.storageIncludedGB !== undefined) result.storage_included_gb = plan.storageIncludedGB;
  if (plan.maxAgents !== undefined) result.max_agents = plan.maxAgents;
  if (plan.extraAgentPrice !== undefined) result.extra_agent_price = plan.extraAgentPrice;
  if (plan.extraAgentStorageGB !== undefined)
    result.extra_agent_storage_gb = plan.extraAgentStorageGB;
  if (plan.extraStoragePrice !== undefined) result.extra_storage_price = plan.extraStoragePrice;
  if (plan.extraStorageAmountGB !== undefined)
    result.extra_storage_amount_gb = plan.extraStorageAmountGB;
  if (plan.features !== undefined) result.features = plan.features;
  if (plan.stripePriceId !== undefined) result.stripe_price_id = plan.stripePriceId;
  if (plan.stripeProductId !== undefined) result.stripe_product_id = plan.stripeProductId;
  if (plan.isActive !== undefined) result.is_active = plan.isActive;
  if (plan.isPublic !== undefined) result.is_public = plan.isPublic;
  if (plan.sortOrder !== undefined) result.sort_order = plan.sortOrder;
  if (plan.baseMargin !== undefined) result.base_margin = plan.baseMargin;
  if (plan.syncWithStripe !== undefined) result.sync_with_stripe = plan.syncWithStripe;

  return result;
}

export const planApi = {
  // ============================================
  // Public Endpoints
  // ============================================

  async getPublicPlans(): Promise<Plan[]> {
    const response = await fetch(`${API_BASE}/plans`);
    if (!response.ok) throw new Error('Failed to fetch plans');
    const data = await response.json();
    return (data.plans || []).map(transformPlan);
  },

  async getBillingPlans(): Promise<Plan[]> {
    const response = await fetch(`${API_BASE}/billing/plans`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch billing plans');
    const data = await response.json();
    return (data.plans || []).map(transformPlan);
  },

  // ============================================
  // Admin Endpoints
  // ============================================

  async getPlans(activeOnly = false, publicOnly = false): Promise<Plan[]> {
    const params = new URLSearchParams();
    if (activeOnly) params.append('active_only', 'true');
    if (publicOnly) params.append('public_only', 'true');

    const response = await fetch(`${API_BASE}/admin/plans?${params}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch plans');
    const data = await response.json();
    return (data.plans || []).map(transformPlan);
  },

  async getPlan(id: string): Promise<Plan> {
    const response = await fetch(`${API_BASE}/admin/plans/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch plan');
    const data = await response.json();
    return transformPlan(data.plan);
  },

  async createPlan(plan: CreatePlanRequest): Promise<Plan> {
    const response = await fetch(`${API_BASE}/admin/plans`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(transformPlanRequest(plan)),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create plan');
    }
    const data = await response.json();
    return transformPlan(data.plan);
  },

  async updatePlan(id: string, plan: Partial<CreatePlanRequest>): Promise<Plan> {
    const response = await fetch(`${API_BASE}/admin/plans/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(transformPlanRequest(plan)),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update plan');
    }
    const data = await response.json();
    return transformPlan(data.plan);
  },

  async deletePlan(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/admin/plans/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to delete plan');
  },

  // ============================================
  // Subscription & Add-on Endpoints
  // ============================================

  async getSubscription(): Promise<SubscriptionResponse> {
    const response = await fetch(`${API_BASE}/billing/subscription/v2`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch subscription');
    const data = await response.json();
    if (data.subscription) {
      data.subscription = {
        ...data.subscription,
        extraAgents: data.subscription.extra_agents || data.subscription.extraAgents || 0,
        extraStorageGB: data.subscription.extra_storage_gb || data.subscription.extraStorageGB || 0,
        extraAgentCost: data.subscription.extra_agent_cost || data.subscription.extraAgentCost || 0,
        extraStorageCost:
          data.subscription.extra_storage_cost || data.subscription.extraStorageCost || 0,
        effectiveAgentLimit:
          data.subscription.effective_agent_limit || data.subscription.effectiveAgentLimit || 0,
        effectiveStorageGB:
          data.subscription.effective_storage_gb || data.subscription.effectiveStorageGB || 0,
        totalMonthlyCost:
          data.subscription.total_monthly_cost ||
          data.subscription.totalMonthlyCost ||
          data.subscription.planAmount,
      };
    }
    return data;
  },

  async addAddon(request: AddAddonRequest): Promise<AddAddonResponse> {
    const response = await fetch(`${API_BASE}/billing/addons`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        addon_type: request.addonType,
        quantity: request.quantity,
      }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to add addon');
    }
    return response.json();
  },

  async removeAddon(addonType: 'EXTRA_AGENT' | 'EXTRA_STORAGE'): Promise<{
    message: string;
    addon_type: string;
    new_limits: { agent_limit: number; storage_gb: number };
  }> {
    const response = await fetch(`${API_BASE}/billing/addons`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        addon_type: addonType,
      }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to remove addon');
    }
    return response.json();
  },

  async getSubscriptionAddons(): Promise<SubscriptionAddon[]> {
    const response = await fetch(`${API_BASE}/billing/subscription/addons`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch addons');
    const data = await response.json();
    return data.addons;
  },

  // ============================================
  // Stripe Sync Endpoints
  // ============================================

  async getStripeStatus(): Promise<{ enabled: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/admin/stripe-status`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch Stripe status');
    return response.json();
  },

  async syncPlanWithStripe(planId: string): Promise<{
    success: boolean;
    sync_result?: {
      base_product_id: string;
      base_price_id: string;
      agent_addon_product_id?: string;
      agent_addon_price_id?: string;
      storage_addon_product_id?: string;
      storage_addon_price_id?: string;
      errors?: string[];
    };
    plan?: Plan;
    error?: string;
  }> {
    const response = await fetch(`${API_BASE}/admin/plans/${planId}/sync-stripe`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sync with Stripe');
    }
    const data = await response.json();
    return {
      ...data,
      plan: data.plan ? transformPlan(data.plan) : undefined,
    };
  },
};
